import React from 'react';
import { useTranslation } from 'react-i18next';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import Avatar from './Avatar';
import { T, horodatageCourt } from './utils';

/**
 * Colonne de gauche : recherche, puis la liste.
 *
 * La ligne active se marque par un fond vert pâle - sans barre latérale.
 * Un URGENT non lu passe sa pastille en rouge - la conversation est déjà remontée
 * en tête par le serveur.
 */
const Squelette = () => (
  <div style={{ padding: '8px 16px' }}>
    {[0, 1, 2, 3, 4].map((i) => (
      <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0' }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: T.fondBulle }} />
        <div style={{ flex: 1 }}>
          <div style={{ height: 14, width: '55%', background: T.fondBulle, borderRadius: 4, marginBottom: 8 }} />
          <div style={{ height: 12, width: '85%', background: T.fondBulle, borderRadius: 4 }} />
        </div>
      </div>
    ))}
  </div>
);

export default function ListeConversations({ conversations, chargement, actifId, filtre, surFiltre, surOuvrir, surNouvelle }) {
  const { t } = useTranslation('conversations');
  const f = filtre.trim().toLowerCase();
  const visibles = f ? conversations.filter((c) => c.titre.toLowerCase().includes(f) || (c.apercu || '').toLowerCase().includes(f)) : conversations;

  return (
    <div style={{ width: 320, flexShrink: 0, borderRight: `1px solid ${T.bord}`, background: T.blanc, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ padding: 16, borderBottom: `1px solid ${T.bord}`, display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <SearchOutlined style={{ position: 'absolute', left: 10, top: 11, color: T.faible, fontSize: 14 }} />
          <input
            type="search" value={filtre} onChange={(e) => surFiltre(e.target.value)} placeholder={t('liste.rechercher')}
            aria-label={t('liste.rechercher')}
            style={{
              width: '100%', height: 36, boxSizing: 'border-box', padding: '0 12px 0 32px', font: 'inherit', fontSize: T.taille.meta,
              border: `1px solid ${T.bord}`, borderRadius: T.rayon.champ, outline: 'none', background: T.fond,
            }}
          />
        </div>
        <button type="button" onClick={surNouvelle} aria-label={t('liste.nouvelle')} title={t('liste.nouvelle')}
          style={{
            width: 36, height: 36, border: 'none', borderRadius: T.rayon.champ, background: T.vert, color: '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
          <PlusOutlined />
        </button>
      </div>

      <div role="list" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {chargement && conversations.length === 0 && <Squelette />}
        {!chargement && conversations.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: T.muet, fontSize: T.taille.meta, lineHeight: 1.5 }}>
            {t('liste.aucune')}<br />
            <button type="button" onClick={surNouvelle}
              style={{ marginTop: 8, border: 'none', background: 'none', color: T.vert, fontWeight: 600, cursor: 'pointer', font: 'inherit' }}>
              {t('liste.demarrer')}
            </button>
          </div>
        )}
        {!chargement && conversations.length > 0 && visibles.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: T.muet, fontSize: T.taille.meta }}>{t('liste.aucuneCorrespond')}</div>
        )}
        {visibles.map((c) => {
          const active = c.conversationTrackingId === actifId;
          const nonLus = c.nonLus > 0;
          return (
            <button
              key={c.conversationTrackingId} type="button" role="listitem" onClick={() => surOuvrir(c.conversationTrackingId)}
              aria-current={active ? 'true' : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer',
                padding: '10px 16px', border: 'none', background: active ? T.vertPale : 'transparent',
                font: 'inherit', minHeight: 60,
              }}
            >
              <Avatar nom={c.titre} initiales={c.initiales} taille={40} presence={c.type === 'DIRECT'} enLigne={c.interlocuteurEnLigne} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{
                    flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontSize: T.taille.corps, fontWeight: nonLus ? 600 : 500, color: T.texte,
                  }}>{c.titre}</span>
                  <span style={{ fontSize: T.taille.mini, color: nonLus ? T.vert : T.faible, flexShrink: 0 }}>{horodatageCourt(c.dernierMessageLe)}</span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                  <span style={{
                    flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontSize: T.taille.meta, color: nonLus ? T.texte2 : T.muet,
                  }}>
                    {c.apercuPriorite === 'URGENT' && <span style={{ color: T.rouge, fontWeight: 700 }}>URGENT · </span>}
                    {c.apercuPriorite === 'IMPORTANT' && <span style={{ color: T.ambre, fontWeight: 600 }}>{t('liste.important')} · </span>}
                    {c.type !== 'DIRECT' && c.apercuAuteur && <span>{t('liste.auteurApercu', { nom: c.apercuAuteur.split(' ')[0] })}</span>}
                    {c.apercu || <span style={{ fontStyle: 'italic' }}>{t('liste.aucunMessage')}</span>}
                  </span>
                  {nonLus && (
                    <span aria-label={t('liste.nonLus', { n: c.nonLus })} style={{
                      background: c.urgentNonLu ? T.rouge : T.vert, color: '#fff', borderRadius: 10, fontSize: 11, fontWeight: 700,
                      padding: '1px 6px', minWidth: 18, textAlign: 'center', flexShrink: 0,
                    }}>{c.nonLus > 99 ? '99+' : c.nonLus}</span>
                  )}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
